import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowRightLeft, Plus, Trash2 } from "lucide-react";

export default function Transfers() {
  const [transfers, setTransfers] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ from_warehouse_id: "", to_warehouse_id: "", lines: [], notes: "" });

  const load = async () => {
    const [t, i, w] = await Promise.all([
      api.get("/warehouse/transfers"),
      api.get("/inventory/items"),
      api.get("/inventory/warehouses"),
    ]);
    setTransfers(t.data); setItems(i.data); setWarehouses(w.data);
  };
  useEffect(() => { load(); }, []);

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { item_id: "", qty: 0, batch_number: "", unit_cost: 0 }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const updateLine = (i, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));

  const save = async () => {
    if (!form.from_warehouse_id || !form.to_warehouse_id || form.lines.length === 0) {
      toast.error("Pick from/to warehouses + at least one line"); return;
    }
    try {
      await api.post("/warehouse/transfers", form);
      toast.success("Transfer posted — stock moved");
      setOpen(false); setForm({ from_warehouse_id: "", to_warehouse_id: "", lines: [], notes: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="warehouse-transfers">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2">
            <ArrowRightLeft className="w-7 h-7 text-primary" /> Stock Transfers
          </h1>
          <p className="text-muted-foreground">Move stock between warehouses with full audit trail.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="trf-new"><Plus className="w-4 h-4 mr-1" /> New transfer</Button>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Number</TableHead><TableHead>From</TableHead><TableHead>To</TableHead>
            <TableHead className="text-right">Lines</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {transfers.map((t) => (
              <TableRow key={t.id} data-testid={`trf-row-${t.number}`}>
                <TableCell className="font-mono text-xs">{t.number}</TableCell>
                <TableCell>{t.from_warehouse_name}</TableCell>
                <TableCell>{t.to_warehouse_name}</TableCell>
                <TableCell className="text-right">{t.lines?.length}</TableCell>
                <TableCell><Badge>{t.status}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{(t.created_at || "").slice(0, 10)}</TableCell>
              </TableRow>
            ))}
            {transfers.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No transfers yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>New stock transfer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>From warehouse</Label>
                <Select value={form.from_warehouse_id} onValueChange={(v) => setForm({ ...form, from_warehouse_id: v })}>
                  <SelectTrigger data-testid="trf-from"><SelectValue placeholder="From…" /></SelectTrigger>
                  <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>To warehouse</Label>
                <Select value={form.to_warehouse_id} onValueChange={(v) => setForm({ ...form, to_warehouse_id: v })}>
                  <SelectTrigger data-testid="trf-to"><SelectValue placeholder="To…" /></SelectTrigger>
                  <SelectContent>{warehouses.filter((w) => w.id !== form.from_warehouse_id).map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              <Label>Lines</Label>
              <Button variant="outline" size="sm" onClick={addLine}><Plus className="w-4 h-4 mr-1" /> Add</Button>
            </div>
            {form.lines.map((ln, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-7">
                  <Label className="text-[10px]">Item</Label>
                  <Select value={ln.item_id} onValueChange={(v) => updateLine(i, { item_id: v })}>
                    <SelectTrigger data-testid={`trf-line-item-${i}`}><SelectValue placeholder="Item…" /></SelectTrigger>
                    <SelectContent>{items.map((it) => <SelectItem key={it.id} value={it.id}>{it.sku} · {it.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px]">Qty</Label>
                  <Input type="number" value={ln.qty} onChange={(e) => updateLine(i, { qty: parseFloat(e.target.value || "0") })} data-testid={`trf-line-qty-${i}`} />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px]">Batch</Label>
                  <Input value={ln.batch_number} onChange={(e) => updateLine(i, { batch_number: e.target.value })} />
                </div>
                <div className="col-span-1">
                  <Button variant="ghost" size="sm" onClick={() => removeLine(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              </div>
            ))}
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="trf-save">Post transfer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
